import React, { ReactNode } from 'react';
import { ModalsKeys } from 'global';
import { logError } from 'utils/logger';
import { useModal } from 'hooks/useModal';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNotifications } from 'modules/notification';
import { useLazyQuery, useMutation } from '@apollo/client';
import { internalInventoryApi } from 'modules/inventory/api';
import { IconButton, Button, Stack, Tooltip } from '@mui/material';
import { useRoutesVisibilityChecks } from 'hooks/useRoutesVisibilityChecks';
import { UploadFile, Preview, Edit, Save, Search } from '@mui/icons-material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { deskingActions } from '../../../../../model';
import { UPSERT_EQUIPMENT } from '../../../../../api';
import { EquipmentActionsMode, FinanceProgramFormValues, UserActionsEvents } from '../../../../../types';
import { AppPages } from '@trnsact/trnsact-shared-types';

interface Props extends Partial<UserActionsEvents> {
  isEquipmentExist: boolean;
  isEditEquipmentFormMode: boolean;
  onToggleEquipmentFormMode: (status: boolean) => void;
}

export const EquipmentActions = ({
  isEquipmentExist,
  onSyncEquipment,
  onEquipmentCreated,
  onEquipmentUpdated,
  isEditEquipmentFormMode,
  onToggleEquipmentFormMode,
}: Props) => {
  const vo = useSelector((state: any) => state.vo);
  const dispatch = useDispatch();

  const {
    getValues,
    setValue,
    formState: { isDirty },
  } = useFormContext<FinanceProgramFormValues>();

  const { routeAvailabilityChecker } = useRoutesVisibilityChecks();
  const isInventorySearchVisible = routeAvailabilityChecker[AppPages.Inventory]();

  const [upsertEquipmentRequest, { loading }] = useMutation(UPSERT_EQUIPMENT, {
    context: { authRequired: true },
    onCompleted(data) {
      if (onEquipmentUpdated) onEquipmentUpdated(data?.upsertEquipment?.id);
    },
  });

  const { handleOpen } = useModal(ModalsKeys.CreateEquipment);
  const { handleOpen: handleOpenUploadEquipmentImagesDialog } = useModal(
    ModalsKeys.UploadImagesForExistingEquipmentDialog
  );
  const { handleOpen: handleOpenPreviewEquipmentImagesDialog } = useModal(
    ModalsKeys.PreviewExistingEquipmentImagesDialog
  );
  const { handleOpen: handleOpenSearchInventoryDialog } = useModal(ModalsKeys.SearchInventoryDialog);

  const { showNotification } = useNotifications();

  const [fetchInventoryById] = useLazyQuery(internalInventoryApi.getById);

  const equipmentActionsMode = !isEquipmentExist ? EquipmentActionsMode.Create : EquipmentActionsMode.Update;

  const handleInventorySubmit = async (listOfEquipmentId: string[]) => {
    if (listOfEquipmentId.length !== 1) {
      showNotification('Please select exactly one equipment item', { type: 'warning' });
      return;
    }

    const { data, error } = await fetchInventoryById({
      variables: { input: { id: listOfEquipmentId[0] } },
    });

    if (error || !data?.result?.success) {
      showNotification(data?.result?.error ?? 'Failed to fetch equipment data', { type: 'error' });
      return;
    }

    const { details, price, id } = data.result.entity.equipment;

    const fieldsMap: Partial<Record<keyof FinanceProgramFormValues['equipment'], string | number | null | undefined>> =
      {
        quantity: '1',
        make: details.make,
        model: details.model,
        year: details.year,
        selectedEquipment: id,
        mileage: details.mileage,
        stockId: details.stockId,
        newUsed: details.condition,
        serialNumberVin: details.vin,
        retailPrice: price.retailPrice,
        description: details.description,
        class: details.class ? `Class ${details.class}` : '',
        totalWholesaleCost: price.totalWholesaleCost != null ? String(price.totalWholesaleCost) : undefined,
      };

    (Object.entries(fieldsMap) as [keyof FinanceProgramFormValues['equipment'], string][]).forEach(
      ([field, value]) => value != null && value !== '' && setValue(`equipment.${field}`, value)
    );

    showNotification('Equipment data applied successfully');

    if (equipmentActionsMode === EquipmentActionsMode.Create) {
      dispatch(
        deskingActions.setEquipmentData({
          equipments: [
            {
              equipmentId: id,
              make: details.make,
              model: details.model,
              year: details.year,
              mileage: details.mileage,
              newUsed: details.condition,
              stockId: details.stockId,
              quantity: 1,
              serialNumberVin: details.vin,
              additionalDescription: details.description,
              classType: details.class ?? null,
              retailPrice: price.retailPrice ?? 0,
              wholesaleCost: price.wholesaleCost ?? 0,
              totalWholesaleCost: price.totalWholesaleCost ?? 0,
              vendorOpportunityId: vo.vendorOpportunityId ?? '',
              term: null,
              type: null,
              hours: null,
              cabType: null,
              loanType: null,
              attachments: null,
              truckClass: null,
              equipmentIdx: null,
              assetTypeStr: null,
              tradeInValue: null,
              lenderProduct: null,
              amountOwedOnTradeIn: null,
            },
          ],
        })
      );
    }
  };

  const handleCreateEquipment = () => {
    handleOpen({ onEquipmentCreated, onSyncEquipment });
  };

  const handleUpdateEquipment = async () => {
    if (!isDirty) {
      return onToggleEquipmentFormMode(false);
    }

    try {
      const {
        make,
        model,
        selectedEquipment,
        newUsed,
        year,
        quantity,
        mileage,
        totalWholesaleCost,
        stockId,
        retailPrice,
        serialNumberVin,
        description,
        class: classType,
      } = getValues('equipment');

      const input: any = {
        equipmentId: selectedEquipment,
        dynamicsVendorOpportunityId: vo.dynamicsVendorOpportunityId,
        vendorOpportunityId: vo.vendorOpportunityId,
        classType,
        make: make,
        model: model,
        newUsed: newUsed,
        year: year.toString(),
        quantity: quantity ? Number(quantity) : 1,
      };

      if (serialNumberVin) {
        input.serialNumberVin = serialNumberVin?.trim();
      }

      if (mileage) {
        input.mileage = Number(mileage.replace(/,/g, ''));
      }

      if (stockId) {
        input.stockId = stockId;
      }

      if (description) {
        input.additionalDescription = description;
      }

      if (retailPrice) {
        input.retailPrice = +retailPrice;
      }

      if (totalWholesaleCost) {
        input.totalWholesaleCost = +totalWholesaleCost;
      }

      await upsertEquipmentRequest({ variables: { input } });
      onToggleEquipmentFormMode(false);
    } catch (error) {
      logError(error);
    }
  };

  const equipmentId = getValues('equipment.selectedEquipment');

  const equipmentActionsByMode: Record<EquipmentActionsMode, ReactNode> = {
    [EquipmentActionsMode.Create]: (
      <Button size="small" variant="contained" onClick={handleCreateEquipment}>
        Add Equipment
      </Button>
    ),
    [EquipmentActionsMode.Update]: (
      <>
        {isEditEquipmentFormMode ? (
          <IconButton color="primary" disabled={loading} className="icon-btn-bordered" onClick={handleUpdateEquipment}>
            <Tooltip title="Save Equipment changes">
              <Save />
            </Tooltip>
          </IconButton>
        ) : (
          <>
            <Tooltip title="Upload Equipment Images">
              <IconButton
                color="primary"
                disabled={!equipmentId}
                className="icon-btn-bordered"
                onClick={() => {
                  handleOpenUploadEquipmentImagesDialog({ equipmentId });
                }}
              >
                <UploadFile />
              </IconButton>
            </Tooltip>

            <Tooltip title="Preview Equipment Images">
              <IconButton
                color="primary"
                disabled={!equipmentId}
                className="icon-btn-bordered"
                onClick={() => {
                  handleOpenPreviewEquipmentImagesDialog({ equipmentId });
                }}
              >
                <Preview />
              </IconButton>
            </Tooltip>

            <Tooltip title="Update Equipment">
              <IconButton
                size="small"
                color="primary"
                className="icon-btn-bordered"
                onClick={() => {
                  onToggleEquipmentFormMode(true);
                }}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          </>
        )}
      </>
    ),
  };

  return (
    <Stack direction="row" gap="1rem">
      <RenderWithCondition condition={isInventorySearchVisible}>
        <Button
          size="small"
          variant="contained"
          startIcon={<Search />}
          onClick={event => {
            event.stopPropagation();
            handleOpenSearchInventoryDialog({ onSubmit: handleInventorySubmit });
          }}
        >
          Search Inventory
        </Button>
      </RenderWithCondition>

      {equipmentActionsByMode[equipmentActionsMode]}
    </Stack>
  );
};
